import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, DownloadIcon, FilterIcon, CalendarIcon, XIcon, AlertTriangleIcon, ShareIcon, CheckCircle2Icon } from 'lucide-react';
import { useRaastTheme, slideVariants } from './RaastShared';
interface RaastHistoryProps {
  onBack: () => void;
  onHome: () => void;
}
type Step = 'list' | 'detail';

// Shared primary button style: height 55px, width 280px, radius 8px, orange fill
const PRIMARY_BUTTON_CLASS = 'h-[55px] w-[280px] mx-auto rounded-lg font-bold text-[16px] bg-[#F37024] text-white shadow-md flex items-center justify-center disabled:opacity-50';
const MOCK_HISTORY = [{
  id: 'RST-10293847',
  date: '2023-10-25T14:30:00',
  amount: 5000,
  type: 'sent',
  status: 'completed',
  name: 'Al-Fatah Supermarket',
  raastId: 'M-1001',
  refundCount: 0,
  refundStatus: 'none'
}, {
  id: 'RST-56473829',
  date: '2023-10-22T18:45:00',
  amount: 3500,
  type: 'sent',
  status: 'failed',
  name: 'Imtiaz Super Market',
  raastId: 'M-1002',
  refundCount: 0,
  refundStatus: 'none'
}, {
  id: 'RST-55667788',
  date: '2023-10-18T16:05:00',
  amount: 1500,
  type: 'sent',
  status: 'completed',
  name: 'KFC',
  raastId: 'M-1003',
  refundCount: 3,
  refundStatus: 'none'
}, {
  id: 'RST-99887766',
  date: '2023-10-15T10:00:00',
  amount: 25000,
  type: 'sent',
  status: 'completed',
  name: 'Daraz.pk',
  raastId: 'M-1004',
  refundCount: 0,
  refundStatus: 'none'
}];
export function RaastHistory({
  onBack,
  onHome
}: RaastHistoryProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('list');
  const [direction, setDirection] = useState(1);
  const [selectedTx, setSelectedTx] = useState<any>(null);
  const [history, setHistory] = useState(MOCK_HISTORY);
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, sent, received
  const [filterStatus, setFilterStatus] = useState('all'); // all, completed, failed
  // Refund State
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showRefundSuccess, setShowRefundSuccess] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundReason, setRefundReason] = useState('Others');
  const [refundTx, setRefundTx] = useState<any>(null);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const filteredHistory = history.filter(tx => {
    if (filterType !== 'all' && tx.type !== filterType) return false;
    if (filterStatus !== 'all' && tx.status !== filterStatus) return false;
    return true;
  });

  // Header — 16px regular title, icon-only orange home button (no circle/border)
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2235" data-mp="1zyi7fmhnifcp">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2236" data-mp="1uzf4c7l8ej7d">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2237" data-mp="28rvdzr8n1fh9" />
      </button>
      <h1 className={`text-[16px] font-normal flex-1 ml-3 ${textPri}`} data-id="element-2238" data-mp="23x7ktf2ovwwc">{title}</h1>
      <button onClick={onHome} className="w-10 h-10 flex items-center justify-center" data-id="element-2239" data-mp="2imdgwu68d700">
        <HomeIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-2240" data-mp="2doc1cj80jpta" />
      </button>
    </div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2241" data-mp="2rmsdipona8it">
      {renderHeader('Merchant History', onBack)}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2242" data-mp="tauu09a6ksy8">
        <div className="flex items-center justify-between mb-6" data-id="element-2243" data-mp="uqpducuk2q1e">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${cardBg} ${borderClr}`} data-id="element-2244" data-mp="usjql3vobbue">
            <CalendarIcon className={`w-4 h-4 ${textSec}`} data-id="element-2245" data-mp="13rdmst53e07f" />
            <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2246" data-mp="13p14gevf9ntu">
              Last 30 Days
            </span>
          </div>
          <button onClick={() => setShowFilters(true)} className={`p-2 rounded-full border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2247" data-mp="c07rcq0bgqoh">
            <FilterIcon className="w-5 h-5" data-id="element-2248" data-mp="g1syduq66aqr" />
          </button>
        </div>

        <div className="space-y-4 flex-1 overflow-y-auto" data-id="element-2249" data-mp="kzujel75ok4v">
          {filteredHistory.length > 0 ? filteredHistory.map((tx, i) => <div key={i} className={`w-full p-4 rounded-lg border ${cardBg} ${borderClr}`} data-id="element-2250" data-mp="3ohco4jrmtb12" data-mpi={i}>
                <button onClick={() => {
            setSelectedTx(tx);
            navigateTo('detail');
          }} className="w-full flex items-center justify-between mb-4" data-id="element-2251" data-mp="3tfe9xcgfzog8">
                  <div className="flex items-center gap-3" data-id="element-2252" data-mp="3byqrw5s9859f">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'sent' ? 'bg-[#EF4444]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2253" data-mp="3bx5zalpi2we7">
                      <ArrowLeftIcon className={`w-5 h-5 ${tx.type === 'sent' ? 'text-[#EF4444] rotate-45' : 'text-[#22C55E] -rotate-135'}`} data-id="element-2254" data-mp="32zncbbsaq7zo" />
                    </div>
                    <div className="text-left" data-id="element-2255" data-mp="2t66hc4gfamuf">
                      <p className={`text-[14px] font-medium ${textPri}`} data-id="element-2256" data-mp="2o9xv673t5tdr">
                        {tx.name}
                      </p>
                      <p className={`text-[11px] font-normal ${textSec}`} data-id="element-2257" data-mp="2k6rqh3wcga9e">
                        {new Date(tx.date).toLocaleDateString()} •{' '}
                        {new Date(tx.date).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-2258" data-mp="1voiy6oitreih">
                    <p className={`text-[14px] font-medium ${tx.type === 'sent' ? textPri : 'text-[#22C55E]'}`} data-id="element-2259" data-mp="20l9f1e2gee4y">
                      {tx.type === 'sent' ? '-' : '+'}Rs.{' '}
                      {tx.amount.toLocaleString()}
                    </p>
                    <p className={`text-[11px] font-normal ${tx.status === 'completed' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2260" data-mp="2em2b438elha6">
                      {tx.status.toUpperCase()}
                    </p>
                  </div>
                </button>
                {tx.type === 'sent' && tx.status === 'completed' &&
          // Contextual inline action — kept as its own outlined style rather
          // than the 55x280 primary CTA spec, since it lives inside a list card.
          <button onClick={e => {
            e.stopPropagation();
            setRefundTx(tx);
            if (tx.refundCount >= 3) {
              setRefundError('You have availed your refund request, no more requests');
            } else {
              setRefundError('');
            }
            setShowRefundModal(true);
          }} disabled={tx.refundStatus === 'requested'} className={`w-full py-2.5 rounded-lg text-[13px] font-medium border transition-colors ${tx.refundStatus === 'requested' ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/10' : 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10'}`} data-id="element-2261" data-mp="vmctp1qoa48a">
                    {tx.refundStatus === 'requested' ? 'Refund Requested' : 'Request Refund'}
                  </button>}
              </div>) : <div className="text-center py-12" data-id="element-2262" data-mp="2q6egwbwrix25">
              <p className={textSec} data-id="element-2263" data-mp="2q4juet5uewvy">No transactions found.</p>
            </div>}
        </div>
      </div>

      {/* Filters Bottom Sheet — left as already implemented, no styling changes */}
      <AnimatePresence data-id="element-2264" data-mp="2zytvr0crj5it">
        {showFilters && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowFilters(false)} data-id="element-2265" data-mp="34uhmhk6w86qf">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} className={`w-full max-w-[430px] rounded-t-3xl p-6 pb-8 shadow-2xl ${cardBg}`} data-id="element-2266" data-mp="1jho8aekxsm1y">
              <div className="flex justify-between items-center mb-6" data-id="element-2267" data-mp="1uwhtdfnqzci3">
                <h3 className={`text-[18px] font-bold ${textPri}`} data-id="element-2268" data-mp="1pyps3v17qe18">Filters</h3>
                <button onClick={() => setShowFilters(false)} className={textSec} data-id="element-2269" data-mp="1pwn4fhnj8wzo">
                  <XIcon className="w-5 h-5" data-id="element-2270" data-mp="1gykvmfxk501u" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-2271" data-mp="ai8v4879j633">
                <div data-id="element-2272" data-mp="ajaer12ch0wf">
                  <p className={`text-[14px] font-bold mb-3 ${textPri}`} data-id="element-2273" data-mp="afcw0gjoibd2">
                    Status
                  </p>
                  <div className="flex gap-2" data-id="element-2274" data-mp="k0kkob6uaxm0">
                    {['all', 'completed', 'failed'].map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 rounded-full text-[13px] font-bold capitalize border ${filterStatus === status ? 'bg-[#F37024] text-white border-[#F37024]' : `${inputBg} ${textSec} ${borderClr}`}`} data-id="element-2275" data-mp="f2iu75733w0i">
                        {status}
                      </button>)}
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2276" data-mp="1bsa0e1oqn7m5">
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Refund Confirmation Modal — left as already implemented, no styling changes */}
      <AnimatePresence data-id="element-2277" data-mp="2zfxregb5led1">
        {showRefundModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowRefundModal(false)} data-id="element-2278" data-mp="33edcufrl6eoc">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} className={`w-full max-w-[430px] rounded-t-3xl p-6 pb-8 shadow-2xl ${cardBg}`} data-id="element-2279" data-mp="2kpgkihfr1w8r">
              <div className="flex justify-between items-center mb-6" data-id="element-2280" data-mp="7ja7rszzeuei">
                <h3 className={`text-[18px] font-bold ${textPri}`} data-id="element-2281" data-mp="3g42ooolv99m">
                  Request Refund
                </h3>
                <button onClick={() => setShowRefundModal(false)} className={textSec} data-id="element-2282" data-mp="3unzpbkxycimo">
                  <XIcon className="w-5 h-5" data-id="element-2283" data-mp="3ppybpfjapbil" />
                </button>
              </div>

              <div className={`p-4 rounded-2xl border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-[#F9F9F9] border-[#F0E8E3]'} mb-6`} data-id="element-2284" data-mp="r5exzqn6sbgd">
                <div className="flex justify-between items-center mb-2" data-id="element-2285" data-mp="heu065fc7ayi">
                  <span className={textSec} data-id="element-2286" data-mp="cgabhx9czl2g">Merchant</span>
                  <span className={`font-bold ${textPri}`} data-id="element-2287" data-mp="19wpinq2bjrzt">
                    {refundTx?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-2288" data-mp="154y0o3x8aula">
                  <span className={textSec} data-id="element-2289" data-mp="1chdslm3edb1h">Amount</span>
                  <span className={`font-bold ${textPri}`} data-id="element-2290" data-mp="1ciymralsuryt">
                    Rs. {refundTx?.amount.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="mb-6" data-id="element-2291" data-mp="1meicff9rsuf4">
                <label className={`text-[13px] font-bold mb-3 block ${textPri}`} data-id="element-2292" data-mp="1mho2oxu4y2cf">
                  Reason for Refund
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-2293" data-mp="1vfq3pddvcuzp">
                  {['Dispute', 'Fraud', 'Others'].map(reason => <button key={reason} onClick={() => setRefundReason(reason)} className={`px-4 py-2 rounded-full text-[13px] font-bold border ${refundReason === reason ? 'bg-[#F37024] text-white border-[#F37024]' : `${inputBg} ${textSec} ${borderClr}`}`} data-id="element-2294" data-mp="255t3pxjer97j">
                      {reason}
                    </button>)}
                </div>
              </div>

              {refundError ? <div className="text-center py-4" data-id="element-2295" data-mp="3bvdohebh4low">
                  <AlertTriangleIcon className="w-12 h-12 text-[#EF4444] mx-auto mb-4" data-id="element-2296" data-mp="3buks97a55el0" />
                  <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2297" data-mp="3plpk3n79p2ks">
                    {refundError}
                  </p>
                  <button onClick={() => setShowRefundModal(false)} className="mt-6 w-full py-4 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2298" data-mp="3llywttgy94l0">
                    Close
                  </button>
                </div> : <>
                  <div className="flex items-start gap-2 mb-6 p-3 rounded-xl bg-[#F37024]/10 border border-[#F37024]/20" data-id="element-2299" data-mp="31xg1eu5couxv">
                    <AlertTriangleIcon className="w-5 h-5 text-[#F37024] shrink-0 mt-0.5" data-id="element-2300" data-mp="m58xue6500gc" />
                    <p className={`text-[12px] text-[#F37024]`} data-id="element-2301" data-mp="h7qowns4dl8c">
                      Note: A maximum of 3 refund requests are allowed per
                      transaction.
                    </p>
                  </div>

                  <div className="flex gap-3" data-id="element-2302" data-mp="tqcjkxbumixv">
                    <button onClick={() => setShowRefundModal(false)} className={`flex-1 py-4 rounded-2xl font-bold text-[14px] border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2303" data-mp="fps2q6phux70">
                      Cancel
                    </button>
                    <button onClick={() => {
                setHistory(prev => prev.map(t => t.id === refundTx.id ? {
                  ...t,
                  refundCount: (t.refundCount || 0) + 1,
                  refundStatus: 'requested'
                } : t));
                setShowRefundModal(false);
                setShowRefundSuccess(true);
              }} className="flex-1 py-4 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2304" data-mp="1dd0toh89kyge">
                      Confirm
                    </button>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Refund Success Modal — left as already implemented, no styling changes */}
      <AnimatePresence data-id="element-2305" data-mp="1dceggdullll3">
        {showRefundSuccess && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowRefundSuccess(false)} data-id="element-2306" data-mp="18c9y0ia4qgs8">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} className={`w-full max-w-[340px] rounded-3xl p-6 shadow-2xl text-center ${cardBg} border ${borderClr}`} data-id="element-2307" data-mp="3b19ucvjs8ar6">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 14,
            stiffness: 220,
            delay: 0.1
          }} className="w-16 h-16 mx-auto bg-[#22C55E] rounded-full flex items-center justify-center mb-4 shadow-lg shadow-[#22C55E]/30" data-id="element-2308" data-mp="361oxqrs34ehm">
                <CheckCircle2Icon className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2309" data-mp="1bvbnto463n18" />
              </motion.div>
              <h3 className={`text-[18px] font-bold mb-2 ${textPri}`} data-id="element-2310" data-mp="1pt8lyoy9s1i0">
                Refund Requested
              </h3>
              <p className={`text-[13px] leading-relaxed mb-6 ${textSec}`} data-id="element-2311" data-mp="1ku5q9v46j7mp">
                Your refund request for Rs. {refundTx?.amount.toLocaleString()}{' '}
                to {refundTx?.name} has been submitted successfully.
                <br data-id="element-2312" data-mp="28l1g4mp2rsrb" />
                <br data-id="element-2313" data-mp="28mm6s189qrjw" />
                <span className="font-bold" data-id="element-2314" data-mp="28lueaz9yx9xu">
                  Request ID: REF-{Math.floor(Math.random() * 1000000)}
                </span>
              </p>
              <button onClick={() => setShowRefundSuccess(false)} className="w-full py-3 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2315" data-mp="2n804g8smy71t">
                Done
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
  const renderDetail = () => <motion.div key="detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2316" data-mp="24i1x5uwnwhz8">
      {renderHeader('Transaction Details', () => navigateTo('list', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2317" data-mp="1zifjjkcx8zpw">
        <div className={`rounded-lg p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2318" data-mp="1zj8l86x0wz16">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2319" data-mp="1ll0r06rdgbtp">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${selectedTx?.type === 'sent' ? 'bg-[#EF4444]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2320" data-mp="1qgpj5p12aqfz">
              <ArrowLeftIcon className={`w-8 h-8 ${selectedTx?.type === 'sent' ? 'text-[#EF4444] rotate-45' : 'text-[#22C55E] -rotate-135'}`} data-id="element-2321" data-mp="1tp9yonbp09xp" />
            </div>
            {/* Hero amount — mapped to the Special Heading/Amount token (Light 40px) */}
            <p className={`text-[40px] font-light ${textPri}`} data-id="element-2322" data-mp="28cuxi51jbmzt">
              Rs. {selectedTx?.amount.toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${selectedTx?.status === 'completed' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2323" data-mp="2ce6skq36h0fe">
              {selectedTx?.status.toUpperCase()}
            </p>
          </div>
          <div className="space-y-4" data-id="element-2324" data-mp="23k1vvq1d4ury">
            <div className="flex justify-between" data-id="element-2325" data-mp="2rbpg5p44d7l9">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2326" data-mp="2ra4hpoeop4i4">Reference ID</span>
              <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2327" data-mp="2w4s9bw4zif0x">{selectedTx?.id}</span>
            </div>
            <div className="flex justify-between" data-id="element-2328" data-mp="35f4t5gx39h5y">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2329" data-mp="31eupiawrr2u9">Date & Time</span>
              <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2330" data-mp="3f7kbifcas7rd">
                {new Date(selectedTx?.date).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2331" data-mp="2min63ct6q5g1">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2332" data-mp="2mjerjut2imsl">
                {selectedTx?.type === 'sent' ? 'To' : 'From'}
              </span>
              <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2333" data-mp="2wgt2jew82jdi">{selectedTx?.name}</span>
            </div>
            <div className="flex justify-between" data-id="element-2334" data-mp="2rh6t7gjiln9z">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2335" data-mp="2rgexm5px3pjp">RAAST ID</span>
              <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2336" data-mp="bpana4bi19l9">
                {selectedTx?.raastId}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-4 mt-6" data-id="element-2337" data-mp="esyghl3t0skz">
          {/* Share/Download kept side-by-side as secondary outlined actions —
              radius updated to 8px, but not forced to the single 280px primary spec
              since two buttons share this row. */}
          <button className={`flex-1 h-[55px] rounded-lg font-bold text-[14px] flex items-center justify-center gap-2 border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2338" data-mp="nr25ire007hj">
            <ShareIcon className="w-5 h-5" data-id="element-2339" data-mp="4c2pntts8vv" /> Share
          </button>
          <button className={`flex-1 h-[55px] rounded-lg font-bold text-[14px] flex items-center justify-center gap-2 border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2340" data-mp="52ncjyk4eje3">
            <DownloadIcon className="w-5 h-5" data-id="element-2341" data-mp="38ikood1ue1f2" /> Download
          </button>
        </div>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2342" data-mp="3dfucjo9l250s">
      {step === 'list' && renderList()}
      {step === 'detail' && renderDetail()}
    </AnimatePresence>;
}